import { useState } from 'react';
import { cn } from '../../lib/utils';

/**
 * Tiny inline rename input. Extracted from DraftTemplateOverlay so it can be
 * reused by the linked-draft status bar (F7), the history sidebar row (F7),
 * and the existing template/subsection editors.
 *
 * Behaviour:
 *  - autoFocus on mount
 *  - Enter   → onSave(trimmed)  (no-op if blank)
 *  - Escape  → onCancel
 *  - Blur with non-empty → onSave; blur with blank → onCancel
 */
export interface InlineTitleEditorProps {
  initial: string;
  onSave: (next: string) => void;
  onCancel: () => void;
  /** Tighter typography for use in dense rows (sidebar list, badges). */
  compact?: boolean;
  className?: string;
  placeholder?: string;
}

export function InlineTitleEditor({
  initial,
  onSave,
  onCancel,
  compact,
  className,
  placeholder,
}: InlineTitleEditorProps): JSX.Element {
  const [value, setValue] = useState(initial);
  return (
    <input
      type="text"
      autoFocus
      value={value}
      placeholder={placeholder}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => (value.trim() ? onSave(value.trim()) : onCancel())}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (value.trim()) onSave(value.trim());
        } else if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      }}
      className={cn(
        'w-full bg-background border border-primary rounded px-1.5 py-0.5 text-foreground',
        compact ? 'text-[12px]' : 'text-sm font-medium',
        className,
      )}
    />
  );
}
